import { useState } from "react";
import { createPortal } from "react-dom";
import { TgEmoji } from "@/components/TgEmoji";
import { PopupCloseButton } from "@/components/PopupClose";
import { ActionButton } from "@/components/ActionButton";
import { useRequestWithdrawal } from "@/api/hooks";
import { tg } from "@/lib/telegram";
import { cardDigits, cardMask, formatCard, isValidCard } from "@/lib/card";
import { formatAmount } from "@/lib/utils";

/**
 * WithdrawCardModal (owner 2026-10-19, «як у Findly») — номер картки для
 * виводу реферальних. Відкривається після «Все одно вивести на картку» у
 * WithdrawOfferModal.
 *
 * Два стани одного попапа: ввід картки → «Заявку прийнято».
 *
 * Номер картки ніде не зберігаємо (ні localStorage, ні URL): він живе лише в
 * стані цього попапа. Підставити збережену картку може сама система —
 * `autoComplete="cc-number"` (Keychain / Google Pay), це їхнє захищене
 * сховище, а не наше. Сервер назад віддає лише `•••• 1234`.
 */
export interface WithdrawDone {
  amount: number;
  cardMask: string;
}

export function WithdrawCardModal({
  balance,
  onClose,
  demo,
  demoDone,
}: {
  balance: number;
  onClose: () => void;
  /** Галерея попапів: «Вивести» не йде на сервер, одразу показує успіх. */
  demo?: boolean;
  /** Галерея попапів: відкрити одразу стан «Заявку прийнято». */
  demoDone?: WithdrawDone;
}) {
  const mutation = useRequestWithdrawal();
  const [digits, setDigits] = useState("");
  const [left, setLeft] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState<WithdrawDone | null>(demoDone ?? null);

  const valid = isValidCard(digits);
  // Помилку показуємо, коли номер уже набрано (16 цифр) або людина вийшла з
  // поля, — а не на кожній цифрі, поки вона ще друкує.
  const complain = !valid && digits.length > 0 && (digits.length >= 16 || left);
  const hint =
    serverError ??
    (complain
      ? digits.length < 13
        ? "У номері картки має бути 16 цифр."
        : "У номері картки помилка — перевір цифри."
      : null);

  const submit = async () => {
    if (!valid || mutation.isPending) return;
    setServerError(null);
    if (demo) {
      setDone({ amount: balance, cardMask: cardMask(digits) });
      return;
    }
    try {
      const r = await mutation.mutateAsync(digits);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      setDigits("");
      setDone({ amount: r.requested_uah, cardMask: r.card_mask });
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      setServerError((e as Error).message);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {done ? (
          <div className="fade-in text-center">
            <div className="mb-2 flex justify-center">
              <TgEmoji char="✅" size={48} />
            </div>
            <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight mb-1.5 [text-wrap:balance]">
              Заявку прийнято
            </div>
            <div className="text-[0.8125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
              Переказ{" "}
              <b className="text-ink whitespace-nowrap">
                {formatAmount(done.amount)}&nbsp;₴
              </b>{" "}
              на картку{" "}
              <b className="text-ink whitespace-nowrap">{done.cardMask}</b> робимо
              вручну. Щойно надішлемо, у боті прийде повідомлення.
            </div>
            <ActionButton className="mt-5" onClick={onClose}>
              Зрозуміло
            </ActionButton>
          </div>
        ) : (
          <>
            <PopupCloseButton onClose={onClose} />
            <div className="text-center mb-4">
              <div className="mb-2 flex justify-center">
                <TgEmoji char="💸" size={48} />
              </div>
              <div className="font-display text-[1.1875rem] font-bold text-ink leading-tight mb-1.5 [text-wrap:balance]">
                Куди вивести гроші?
              </div>
              <div className="text-[0.8125rem] text-ink-dim leading-relaxed [text-wrap:balance]">
                Вкажи номер картки, на яку переказати{" "}
                <b className="text-ink whitespace-nowrap">
                  {formatAmount(balance)}&nbsp;₴
                </b>
                .
              </div>
            </div>

            <div className="rounded-2xl bg-bg/50 p-3">
              <label
                htmlFor="withdraw-card"
                className="block font-mono text-[0.625rem] uppercase tracking-[0.08em] text-ink-dim mb-1"
              >
                Номер картки
              </label>
              <input
                id="withdraw-card"
                type="text"
                inputMode="numeric"
                autoComplete="cc-number"
                placeholder="0000 0000 0000 0000"
                value={formatCard(digits)}
                onChange={(e) => {
                  setDigits(cardDigits(e.target.value));
                  setServerError(null);
                }}
                onBlur={() => setLeft(true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void submit();
                }}
                className={
                  "w-full rounded-xl bg-surface px-3 py-2.5 font-display text-[1.0625rem] " +
                  "font-bold tracking-[0.04em] tabular-nums text-ink outline-none ring-1 " +
                  "placeholder:font-semibold placeholder:text-ink-faint transition " +
                  (hint ? "ring-red-400" : "ring-ink/10 focus:ring-primary/40")
                }
              />
              <div
                className={
                  "mt-1.5 text-[0.6875rem] leading-snug [text-wrap:balance] " +
                  (hint ? "text-red-500" : "text-ink-faint")
                }
              >
                {hint ?? "16 цифр з лицьового боку картки."}
              </div>
            </div>

            <ActionButton
              className="mt-4"
              disabled={!valid || mutation.isPending}
              onClick={submit}
            >
              {mutation.isPending
                ? "Надсилаю заявку…"
                : `Вивести ${formatAmount(balance)} ₴`}
            </ActionButton>
            <div className="mt-2.5 text-center text-[0.6875rem] text-ink-faint leading-snug [text-wrap:balance]">
              Гроші переказуємо вручну. Щойно надішлемо, у боті прийде
              повідомлення.
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
